/**
 * 🛡️ ERROR UTILITIES
 * Type-safe error handling utilities for Clean Architecture compliance.
 * Eliminates `any` type usage in catch blocks.
 */

/**
 * Safely extracts error message from unknown error types.
 * Use this in catch blocks instead of `err.message` when `err: unknown`.
 */
export function getErrorMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    if (err && typeof err === 'object' && 'message' in err) {
        return String((err as { message: unknown }).message);
    }
    return 'An unknown error occurred';
}

/**
 * Type guard to check if an error has a message property.
 */
export function isErrorWithMessage(err: unknown): err is { message: string } {
    return (
        typeof err === 'object' &&
        err !== null &&
        'message' in err &&
        typeof (err as Record<string, unknown>).message === 'string'
    );
}

/**
 * Safely extracts HTTP status code from error objects.
 */
export function getErrorStatus(err: unknown): number | undefined {
    if (err && typeof err === 'object' && 'status' in err) {
        const status = (err as { status: unknown }).status;
        return typeof status === 'number' ? status : undefined;
    }
    return undefined;
}

/**
 * Safely extracts error code from Supabase/Postgres errors.
 */
export function getErrorCode(err: unknown): string | undefined {
    if (err && typeof err === 'object' && 'code' in err) {
        return String((err as { code: unknown }).code);
    }
    return undefined;
}
